import { parseExchangeField } from '@/lib/proto/util'
import type { SelectedInstrument, SelectedOption } from '@/types/dashboard'
import { Exchange, MarketDataType } from '@/types/generated/common/types'

type OptionLeg = Pick<SelectedOption, 'instrumentName' | 'exchange'> & {
  ticker?: { exchange: Exchange }
}
type InstrumentLeg = Pick<SelectedInstrument, 'symbol' | 'exchange'>

export interface PriceSyncPayload {
  symbol: string
  exchange: Exchange
  dataType: MarketDataType
}

/** Ticker subscriptions for the order form's legs, each on the leg's OWN exchange.
 * The card's primary exchange is only a fallback for a leg that carries none — keying
 * on it subscribed a Bybit leg on a Deribit-tab card to a Deribit ticker that never
 * arrives, leaving the price (and so the preview) empty. */
export function buildPriceSyncPayloads(
  options: OptionLeg[],
  instruments: InstrumentLeg[],
  primaryExchange: Exchange
): PriceSyncPayload[] {
  const legs = [
    ...options.map((o) => ({
      symbol: o.instrumentName,
      exchange: o.exchange ?? (o.ticker ? String(o.ticker.exchange) : undefined),
    })),
    ...instruments.map((i) => ({ symbol: i.symbol, exchange: i.exchange })),
  ]
  const seen = new Set<string>()
  const payloads: PriceSyncPayload[] = []
  for (const leg of legs) {
    const exchange = parseExchangeField(leg.exchange, primaryExchange)
    if (!leg.symbol || exchange === Exchange.EXCHANGE_UNSPECIFIED) continue
    const key = `${exchange}:${leg.symbol}`
    if (seen.has(key)) continue
    seen.add(key)
    payloads.push({
      symbol: leg.symbol,
      exchange,
      dataType: MarketDataType.MARKET_DATA_TYPE_TICKER,
    })
  }
  return payloads
}
